import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { PushUnitActivation } from '@apps-common/config/push-notification'
import { useNotifications } from '@apps-common/contexts/NotificationContext'
import { INotificationPayload } from '@apps-common/types/portal/push-registration'
import { IActivationServiceRequestModel } from '@apps-common/types/portal/service-request'

export interface IListeningActivationRequest {
  request: IActivationServiceRequestModel
  callback: (request: IActivationServiceRequestModel) => void
  refresh: () => void
}

type IUnitActivationContext = {
  subscribe: (listener: IListeningActivationRequest) => void
  unsubscribe: (id: number) => void
}

const UnitActivationContext = createContext<IUnitActivationContext>({
  subscribe: (listener: IListeningActivationRequest) => undefined,
  unsubscribe: (id: number) => undefined
})

export const UnitActivationProvider = ({ children }: any) => {
  const notifications = useNotifications()

  const [timerId, setTimerId] = useState<number | undefined>()
  const [listeners, setListeners] = useState<IListeningActivationRequest[]>([])
  const listenersRef = useRef(listeners)
  const intervalMsRef = useRef<number | undefined>(undefined)

  const broadcastToListeners = (id: number, status: string, statusDesc: string) => {
    const listener = listenersRef.current.find((l) => l.request.unitActivationRequestId === id)

    if (listener) {
      listener.callback({
        ...listener.request,
        status,
        statusDescription: statusDesc
      })
    }
  }

  useEffect(() => {
    listenersRef.current = listeners
  }, [listeners])

  const onNotificationReceived = (event: INotificationPayload) => {
    const { data } = event
    const { requestId, status, statusDesc } = data || {}

    if (requestId && status) {
      const activationRequestId = parseInt(requestId, 10)

      broadcastToListeners(activationRequestId, status, statusDesc)
    }
  }

  const processQueue = async () => {
    const callbacks: any[] = listenersRef.current.map((l) => l.refresh)
    const staggerDelayMs = Math.min(1000 + listenersRef.current.length * 250, 5000)

    for (let i = 0; i < callbacks.length; i += 1) {
      const callback = callbacks[i]

      if (callback) {
        callback()
        await new Promise((resolve) => {
          setTimeout(resolve, staggerDelayMs)
        }) // stagger callbacks to reduce bursts
      }
    }
  }

  const timerTick = () => {
    if (listenersRef.current.length > 0) {
      processQueue()
    }
  }

  const getIntervalMs = (count: number) => {
    const extraDelay = Math.min(Math.max(count - 1, 0) * 1000, 30000)
    return 30000 + extraDelay
  }

  const startTimer = (intervalMs: number) => {
    const id = window.setInterval(() => timerTick(), intervalMs)

    setTimerId(id)
    intervalMsRef.current = intervalMs
  }

  useEffect(() => {
    if (listeners.length > 0) {
      const nextIntervalMs = getIntervalMs(listeners.length)

      if (!timerId) {
        startTimer(nextIntervalMs)
        return
      }

      if (intervalMsRef.current !== nextIntervalMs) {
        window.clearInterval(timerId)
        setTimerId(undefined)
        startTimer(nextIntervalMs)
      }
    } else if (timerId) {
      window.clearInterval(timerId)
      setTimerId(undefined)
      intervalMsRef.current = undefined
    }
  }, [listeners, timerId])

  useEffect(
    () => () => {
      if (timerId) {
        window.clearInterval(timerId)
      }
    },
    [timerId]
  )

  useEffect(() => {
    if (notifications) {
      notifications.subscribeToNotifications({
        id: 'unit-activation-context',
        destination: PushUnitActivation.destination,
        onNotificationReceived
      })
    }
    return () => {
      notifications?.unsubscribeToNotifications('unit-activation-context')
    }
  }, [notifications])

  const subscribe = useCallback((listener: IListeningActivationRequest) => {
    setListeners((prevState) => {
      const index = prevState.findIndex((l) => l.request.unitActivationRequestId === listener.request.unitActivationRequestId)

      if (index === -1) {
        return [...prevState, listener]
      }

      const updated = [...prevState]
      updated[index] = listener
      return updated
    })
  }, [])

  const unsubscribe = useCallback((id: number) => {
    setListeners((prevState) => prevState.filter((l) => l.request.unitActivationRequestId !== id))
  }, [])

  const value = useMemo(
    () => ({
      subscribe,
      unsubscribe
    }),
    [subscribe, unsubscribe]
  )

  return <UnitActivationContext.Provider value={value}>{children}</UnitActivationContext.Provider>
}

export const useUnitActivationContext = () => useContext(UnitActivationContext)
